import { useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Chip, Collapse, Tooltip, Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { SEO_COLORS } from "./seoColors";

const STATUS_CONFIG = {
  pass: { icon: CheckCircleOutlineIcon, color: SEO_COLORS.pass },
  warn: { icon: WarningAmberIcon,       color: SEO_COLORS.warn },
  fail: { icon: CancelOutlinedIcon,     color: SEO_COLORS.fail },
};

const STATUS_ORDER = { fail: 0, warn: 1 };


function mergeFindings(checks, priorityFixes) {
  const fixesByTitle = {};
  (priorityFixes || []).forEach(fix => {
    if (fix.title) fixesByTitle[fix.title] = fix;
  });

  return (checks || []).map(check => {
    // Normalize "warning" → "warn" (ranklens uses the full word, frontend uses short form)
    const status = check.status === "warning" ? "warn" : check.status;
    if (status === "pass") return { ...check, status, impact_points: 0 };

    const fix = fixesByTitle[check.check];
    return {
      ...check,
      status,
      impact_points: fix?.impact_points ?? 0,
      why: fix?.why,
      how: fix?.how,
      how_steps: fix?.how_steps,
    };
  });
}

// ── IssueCard ────────────────────────────────────────────────────────────────

function IssueCard({ item, showCategory, text }) {
  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.warn;
  const Icon = cfg.icon;
  const hasDetail = item.why || item.how || item.detail || item.benchmark || item.suggestion;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: "none",
        "&:before": { display: "none" },
        borderLeft: "3px solid",
        borderLeftColor: cfg.color,
        borderRadius: "0 4px 4px 0 !important",
        pl: 1,
        mb: 0.25,
        transition: "background-color 0.15s",
        "&:hover": { bgcolor: "background.secondary" },
      }}
    >
      <AccordionSummary
        expandIcon={hasDetail ? <ExpandMoreIcon fontSize="small" /> : null}
        sx={{ px: 0, minHeight: 48, alignItems: "center" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%", pr: 1 }}>
          <Icon sx={{ color: cfg.color, fontSize: 18, flexShrink: 0 }} />

          {item.impact_points > 0 && (
            <Box sx={{
              background: "linear-gradient(135deg, #dc2626, #f59e0b)",
              px: 0.75, borderRadius: 0.75, flexShrink: 0,
              lineHeight: "20px",
            }}>
              <Typography variant="caption" fontWeight={700} component="span" sx={{ color: "#fff" }}>
                +{item.impact_points}pt
              </Typography>
            </Box>
          )}

          <Typography variant="body2" sx={{ flex: 1 }}>{item.check}</Typography>

          {showCategory && (
            <Chip
              label={item.category}
              size="small"
              sx={{
                height: 20, fontSize: 11, flexShrink: 0,
                bgcolor: "action.selected", color: "text.secondary",
                fontWeight: 600,
              }}
            />
          )}

          {item.value != null && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ flexShrink: 0, maxWidth: 260, textAlign: "right" }}
            >
              {String(item.value)}
            </Typography>
          )}
        </Box>
      </AccordionSummary>

      {hasDetail && (
        <AccordionDetails sx={{ px: 2, pt: 1, pb: 1.5, bgcolor: "background.secondary", borderRadius: 1 }}>
          {item.why && (
            <Typography variant="body2" sx={{ mb: 0.75 }}>
              <strong>{text.whyLabel}:</strong> {item.why}
            </Typography>
          )}
          {(item.how || item.how_steps?.length > 0) && (() => {
            const steps = item.how_steps?.length > 0 ? item.how_steps : [item.how];
            return (
              <Box sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{text.howLabel}:</strong>
                </Typography>
                {steps.length === 1 ? (
                  <Typography variant="body2" color="text.secondary">{steps[0]}</Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {steps.map((step, i) => (
                      <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {step}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })()}
          {item.detail && !item.why && (
            <Typography variant="body2" sx={{ mb: 0.75 }}>{item.detail}</Typography>
          )}
          {item.benchmark && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {text.optimalLabel}: {item.benchmark}
            </Typography>
          )}
          {item.suggestion && !item.how && (
            <Typography variant="caption" color="primary" display="block">
              {item.suggestion}
            </Typography>
          )}
        </AccordionDetails>
      )}
    </Accordion>
  );
}

// ── PassesSummary ────────────────────────────────────────────────────────────

function PassesSummary({ passes, showCategory, text }) {
  const [open, setOpen] = useState(false);
  if (!passes.length) return null;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
          py: 0.75, px: 1, borderRadius: 1,
          "&:hover": { bgcolor: "background.secondary" },
        }}
      >
        <CheckCircleOutlineIcon sx={{ color: SEO_COLORS.pass, fontSize: 16 }} />
        <Typography variant="caption" color="text.secondary">
          {passes.length} {text.checksPassed}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 14, color: "text.disabled", ml: "auto",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        />
      </Box>
      <Collapse in={open}>
        <Box sx={{ pl: 1, opacity: 0.7 }}>
          {passes.map((item, i) => (
            <IssueCard key={item.check || i} item={item} showCategory={showCategory} text={text} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────

export default function ReportFindings({ checks, priorityFixes, activeCategory, onClearCategory, text }) {
  const t = text.findings;

  const CATEGORY_LABELS = {
    "SEO Technical": t.seoCategory,
    "Content":       t.contentCategory,
    "GEO":           t.geoCategory,
    "Performance":   t.performanceCategory,
  };

  const merged = mergeFindings(checks, priorityFixes);
  const filtered = activeCategory ? merged.filter(c => c.category === activeCategory) : merged;

  const issues = filtered
    .filter(c => c.status !== "pass")
    .sort((a, b) => {
      const pts = (b.impact_points || 0) - (a.impact_points || 0);
      if (pts !== 0) return pts;
      return (STATUS_ORDER[a.status] ?? 2) - (STATUS_ORDER[b.status] ?? 2);
    });

  const passes = filtered.filter(c => c.status === "pass");
  const fails = issues.filter(c => c.status === "fail");
  const warns = issues.filter(c => c.status === "warn");
  const showCategory = !activeCategory;
  const total = filtered.length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
          {activeCategory ? (CATEGORY_LABELS[activeCategory] ?? activeCategory) : t.title}
        </Typography>
        {activeCategory && (
          <Chip
            label={t.clearFilter}
            size="small"
            variant="outlined"
            onDelete={onClearCategory}
            onClick={onClearCategory}
            sx={{ fontSize: 12 }}
          />
        )}
      </Box>

      {/* Distribution bar */}
      {total > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", height: 10, borderRadius: "5px", overflow: "hidden", gap: "2px" }}>
            {fails.length > 0 && (
              <Box sx={{ flex: fails.length, background: "linear-gradient(90deg, #b71c1c, #d32f2f)", borderRadius: warns.length === 0 && passes.length === 0 ? "5px" : "5px 0 0 5px" }} />
            )}
            {warns.length > 0 && (
              <Box sx={{ flex: warns.length, background: "linear-gradient(90deg, #f57c00, #ff9800)" }} />
            )}
            {passes.length > 0 && (
              <Box sx={{ flex: passes.length, background: "linear-gradient(90deg, #2e7d32, #43a047)", borderRadius: fails.length === 0 && warns.length === 0 ? "5px" : "0 5px 5px 0" }} />
            )}
          </Box>
          <Box sx={{ display: "flex", mt: 0.75 }}>
            {fails.length > 0 && (
              <Box sx={{ flex: fails.length, textAlign: "right" }}>
                <Typography variant="caption" sx={{ color: SEO_COLORS.fail, fontWeight: 600 }}>
                  {fails.length} {t.failSuffix || "fail"}
                </Typography>
              </Box>
            )}
            {warns.length > 0 && (
              <Box sx={{ flex: warns.length, textAlign: "right" }}>
                <Typography variant="caption" sx={{ color: SEO_COLORS.warn, fontWeight: 600 }}>
                  {warns.length} {t.warnSuffix || "warn"}
                </Typography>
              </Box>
            )}
            {passes.length > 0 && (
              <Box sx={{ flex: passes.length, textAlign: "right" }}>
                <Typography variant="caption" sx={{ color: SEO_COLORS.pass, fontWeight: 600 }}>
                  {passes.length} {t.passedSuffix}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Issues */}
      {issues.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
          {t.noIssues}
        </Typography>
      ) : (activeCategory === "GEO" || activeCategory === "SEO Technical" || activeCategory === "Content") ? (
        // Split issues into AI-fixable vs platform/website-level using backend flag
        (() => {
          const fixableIssues = issues.filter(c => c.fixable_by_ai !== false);
          const platformIssues = issues.filter(c => c.fixable_by_ai === false);
          return (
            <>
              {fixableIssues.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="#2e7d32" sx={{ display: "block", mb: 0.75, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {t.geoContentGroup}
                  </Typography>
                  {fixableIssues.map((item, i) => (
                    <IssueCard key={item.check || i} item={item} showCategory={false} text={t} />
                  ))}
                </Box>
              )}
              {platformIssues.length > 0 && (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                    <Typography variant="caption" fontWeight={700} color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {t.geoWebsiteGroup}
                    </Typography>
                    <Tooltip title={t.platformMetadataDisclaimer} placement="top" arrow>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                    </Tooltip>
                  </Box>
                  {platformIssues.map((item, i) => (
                    <IssueCard key={item.check || i} item={item} showCategory={false} text={t} />
                  ))}
                </Box>
              )}
            </>
          );
        })()
      ) : (
        issues.map((item, i) => (
          <IssueCard key={item.check || i} item={item} showCategory={showCategory} text={t} />
        ))
      )}

      {/* Passes */}
      <PassesSummary key={activeCategory} passes={passes} showCategory={showCategory} text={t} />
    </Box>
  );
}
